/**
 * Manga comic product types
 *
 * A manga book is a distinct PRODUCT TYPE (books.product_type = 'manga-comic'),
 * not just an art style: each page is a full multi-panel comic page with speech
 * balloons, narration boxes and SFX lettering rendered into the artwork, rather
 * than a single illustration with overlaid prose.
 *
 * The full script lives in books.metadata.manga (JSONB); each page row stores
 * its panel script as JSON in pages.basic_scene and a readable transcript in
 * pages.content.
 */
import type { ReaderBand, StoryAct } from '@/utils/manga/comic-story-profile';

/**
 * Comic sub-styles span two traditions rendered by the same pipeline:
 *  - manga:   shonen / shojo / chibi / kodomo (screentone + RTL options apply)
 *  - western: superhero / cartoon / european  (always LTR; B&W renders as classic inks)
 */
export type MangaSubStyle =
  | 'shonen' | 'shojo' | 'chibi' | 'kodomo'
  | 'superhero' | 'cartoon' | 'european';

export type ComicTradition = 'manga' | 'western';

export type MangaColorMode = 'screentone' | 'full-color';

/** ltr = western reading order (default for kids), rtl = authentic Japanese */
export type MangaReadingDirection = 'ltr' | 'rtl';

export type MangaPanelLayout =
  | 'splash'            // single full-page panel
  | 'two-vertical'      // 2 stacked panels
  | 'two-horizontal'    // 2 side-by-side panels
  | 'three-tier'        // 3 stacked rows
  | 'grid-2x2'          // 4 panels
  | 'tall-plus-two'     // 1 tall panel + 2 stacked beside it
  | 'action-diagonal';  // 3-4 panels with slanted gutters for action beats

export type MangaBalloonType = 'speech' | 'thought' | 'shout' | 'whisper' | 'narration';

export type MangaShotType =
  | 'establishing'
  | 'wide'
  | 'medium'
  | 'close-up'
  | 'extreme-close-up'
  | 'action'
  | 'over-the-shoulder';

export interface MangaDialogueLine {
  /** Character name, or 'NARRATOR' for narration boxes */
  speaker: string;
  /** Rendered verbatim inside the balloon — kept short for legibility */
  text: string;
  balloon: MangaBalloonType;
}

export interface MangaPanel {
  /** 1-based position within the page, in reading order */
  panel: number;
  shot: MangaShotType;
  /** Visual description of what happens in this panel */
  sceneDescription: string;
  /** Names of characters visible in this panel */
  characters: string[];
  /** Dominant emotion, drives manga emotion iconography (sweat drop, anger vein...) */
  emotion?: string;
  dialogue: MangaDialogueLine[];
  /** Sound-effect lettering, e.g. "WHOOSH", "TAP TAP" */
  sfx?: string;
}

export interface MangaPageScript {
  /** 1-based story page number (0 is the cover, handled separately) */
  pageNumber: number;
  layout: MangaPanelLayout;
  panels: MangaPanel[];
  /** True when the page ends on a page-turn hook */
  cliffhanger?: boolean;
  /** Picture-book mode only: the page's read-aloud prose, typeset outside the picture (never drawn). */
  narration?: string;
}

/** Picture-book styles the comic engine can draw in picture-book mode ('european' is the comic sub-style's look). */
export type PictureBookStyle = 'watercolor' | 'european' | 'impressionist' | 'oil-painting' | 'anime' | 'graphicnovel' | 'storybook';

export const PICTURE_BOOK_STYLE_IDS: readonly PictureBookStyle[] = ['watercolor', 'european', 'impressionist', 'oil-painting', 'anime', 'graphicnovel', 'storybook'];

/**
 * Picture-book mode of the comic engine (opt-in; absent = comics exactly as before): every page is one full-page
 * picture with no text in it, and the script writes per-page narration that is typeset outside the picture.
 */
export interface PictureBookMode {
  style: PictureBookStyle;
  /** Reader band the book is written for; O and W put the text on a facing page. */
  band?: ReaderBand;
}

export interface MangaCoverScript {
  sceneDescription: string;
  /** Optional short tagline rendered under the title */
  taglineText?: string;
}

export interface MangaVisualRegistry {
  /** name -> complete visual description (same rigor as picture-book registry) */
  characters: Record<string, string>;
  /** recurring non-human elements: pets, robots, vehicles... */
  nonHumanElements?: Record<string, string>;
  /** recurring named or role-named non-cast people ("Grandpa", "Coach Lee") -> one fixed look; never a cast name */
  supportingCharacters?: Record<string, string>;
  /** named locations that recur across pages */
  locations?: Record<string, string>;
}

/** One page of a pitch: what happens on it, written before the page script (no panels yet). */
export interface MangaPageBeat {
  pageNumber: number;
  beat: string;
  /** A locations registry key */
  location?: string;
  /** Names on the page (cast and registered supporting characters) */
  present?: string[];
  cliffhanger?: boolean;
  /** V2: the story act this page belongs to. */
  act?: StoryAct;
  /** V2: the spine set piece this page belongs to (rising/escalation pages). */
  setPieceId?: string;
  /** Master scripts (P3): the master page's picture direction, with this family's names. */
  picture?: string;
  /** Master scripts (P3): the page's adapted text (picture books: fixed narration; comics: the page story and key line). */
  text?: string;
  /** Master scripts with a wardrobe: the master page's outfit variant key (absent = the registered base outfit). */
  wear?: string;
}

/** V2 story spine: written before the beats, in English except `keyLines` (the book's language). */
export interface ComicSpine {
  heroes: string[];
  castRoles: Record<string, string>;
  want: string;
  why: string;
  opposition: string;
  setPieces: { id: string; location: string; heroAction: string; gain: string; causesNext: string }[];
  climaxChoice: { action: string; usesGainIds: string[] };
  echoOfPage1: string;
  setupPayoffs: { item: string; setupPage: number; payoffPage: number }[];
  keyLines: { kind: 'refrain' | 'iconic' | 'climax'; text: string; page?: number }[];
  voiceTags: Record<string, string>;
  theme: string;
}

/** V2: what the story lints found and what the repair did. Lints never block a book. */
export interface ComicStoryQa {
  /** V2: id of the classic card applied to this story. */
  classic?: string;
  /** P3: the master script this story was adapted from. */
  master?: { id: string; version: number };
  /** P3: the master this book was meant to follow but could not (the adapt failed; the story is free-form). */
  masterFallback?: { id: string; version: number; reason: string };
  lintFailures: string[];
  repaired: boolean;
  fallback?: string;
  model?: string;
  ms: Record<string, number>;
  /** Story-model tokens per step (spine, repair); the harness reads them for the cost ceiling. */
  usage?: Record<string, { input: number; output: number }>;
  /** Owner fix 2: nicknames / source names in balloons replaced by the registry name (text only). */
  nameFixes?: { page: number; from: string; to: string }[];
  /** Owner fix 3: iconic / climax key lines removed from pages other than their own (text only). */
  keyLineDedupes?: { page: number; text: string; keyPage: number }[];
  /** Script defect gate (Q1): V2 books record what was repaired and what is left; other books only what was found. */
  defects?: ComicScriptDefects;
  /** Q3 line fit (V2): over-cap balloons the story model rewrote to fit (failed ones fell back to the trim). */
  lineFit?: ComicLineFitQa;
  /** Q3 (V2): over-cap spine key lines rewritten to fit, once, before the chunks were written. */
  keyLineFit?: ComicLineFitQa;
}

/** Q3: one line-fit rewrite call (utils/manga/comic-line-fit). `skipped` says why no call ran. */
export interface ComicLineFitQa {
  /** Lines sent for a rewrite (over the per-balloon cap, or the longest on a page over the per-page cap). */
  overCap: number;
  rewritten: number;
  failed: number;
  ms: number;
  /** Empty pages (no balloon, no caption, not a silent beat) sent for one new line, and how many got a valid one. */
  emptyPages?: number;
  filled?: number;
  skipped?: 'no-time' | 'not-configured';
  error?: string;
}

/** One defect the script gate found (utils/manga/comic-script-defects). Unknown-name text is the word; an empty page has panel 0 and its beat as text. */
export interface ScriptDefect {
  kind: 'cut-off' | 'unknown-name' | 'repeated-line' | 'over-cap' | 'empty-page';
  page: number;
  panel: number;
  text: string;
}

export interface ComicScriptDefects {
  /** V2: repaired before drawing. */
  fixed?: ScriptDefect[];
  /** V2: left in the script (a paid book with any alerts the admin when drawing starts). */
  residual?: ScriptDefect[];
  /** Non-V2: found only; the script text is never changed. */
  detected?: ScriptDefect[];
  /** The book's language when the name check (English only) was skipped. */
  nameCheckSkipped?: string;
}

export interface MangaScript {
  title: string;
  logline: string;
  genre: string;
  cover: MangaCoverScript;
  visualRegistry: MangaVisualRegistry;
  pages: MangaPageScript[];
  /** Pitch-written books only: one beat per page, written with the page script (older stored pitches carry them already). */
  beats?: MangaPageBeat[];
  /** V2 pitches: the hero's concrete, drawable want (<= 120 chars). Absent on older pitches. */
  want?: string;
  /** V2: the story spine written with the beats. */
  spine?: ComicSpine;
  /** V2: story lint failures left after the (at most one) repair. */
  storyQa?: ComicStoryQa;
  /** V2 classics: card characters / locations the pitch model left out and the code registered. */
  classicMerged?: string[];
}

/** True when the script has exactly `pageCount` pages, numbered 1..N in order. */
export function isComicScriptComplete(s: { pages?: unknown[] } | null | undefined, pageCount: number): boolean {
  const pages = Array.isArray(s?.pages) ? s!.pages! : [];
  if (pages.length === 0 || pages.length !== pageCount) return false;
  return pages.every((p, i) => (p as { pageNumber?: unknown } | null)?.pageNumber === i + 1);
}

/**
 * True for a pitch: no pages yet and a visual registry present. Beats are optional: the interactive pitch has none
 * (they are written with the page script); a pitch stored before that change carries them and they are reused.
 */
export function isComicPitch(s: MangaScript | null | undefined): boolean {
  return !!s && (!Array.isArray(s.pages) || s.pages.length === 0)
    && !!s.visualRegistry && typeof s.visualRegistry === 'object';
}

/** Comic lengths offered (story pages). Anything else is coerced to 8. */
export const COMIC_PAGE_COUNTS = [8, 16] as const;
export type ComicPageCount = typeof COMIC_PAGE_COUNTS[number];

/** The square comic (24 story pages, drawn 1:1, 8x8 hardcover). Deliberately NOT in COMIC_PAGE_COUNTS: those are the tall lengths. */
export const COMIC_SQUARE_PAGE_COUNT = 24 as const;
export type AnyComicPageCount = ComicPageCount | typeof COMIC_SQUARE_PAGE_COUNT;
export type ComicShape = 'tall' | 'square';

/** The page count is the only source of truth for shape: 24 is square, anything else tall (a picture book is always tall here). */
export function comicShape(s: { pageCount?: number; pictureBook?: PictureBookMode } | null | undefined): ComicShape {
  if (s?.pictureBook) return 'tall';
  return s?.pageCount === COMIC_SQUARE_PAGE_COUNT ? 'square' : 'tall';
}

/** Picture-book inner pages are drawn square. */
export function pictureBookInnerAspect(): '1:1' {
  return '1:1';
}

export function comicAspect(shape: ComicShape): '2:3' | '1:1' {
  return shape === 'square' ? '1:1' : '2:3';
}

/** Whether a NEW preview is fast (pitch + cover, page script afterwards): 24 pages always, 8/16 with COMIC_FAST_PREVIEW. */
export function comicPreviewIsFast(pageCount: number, fastOn: boolean): boolean {
  return pageCount === COMIC_SQUARE_PAGE_COUNT || fastOn;
}

export interface MangaSettings {
  subStyle: MangaSubStyle;
  colorMode: MangaColorMode;
  readingDirection: MangaReadingDirection;
  pageCount: AnyComicPageCount;
  /** Server-written only, and only for 24-page books; absent on tall books. Any client value is ignored. */
  shape?: 'square';
  /** Who letters the book: 'code' (art drawn with no text, lettered by code) or 'model' (the image model draws it). Stamped by the server at creation; absent = 'model'. */
  lettering?: 'code' | 'model';
  /** Stamped at creation when the storyboarding V2 flag was on; later steps read this, never the flag. */
  storyboard?: 'v2';
  /** Reader band the book is written for (V2 books). */
  readerBand?: ReaderBand;
  /** Picture-book mode of the comic engine (absent = a comic). */
  pictureBook?: PictureBookMode;
}

/** Most people one comic can star (the create-comic UI and POST /api/books/create-manga-preview both use it). */
export const MAX_COMIC_CHARACTERS = 4;

const SIBLING_RELATIONSHIP = /^(?:big|little|lil|older|younger|elder|baby|kid)\s+(brother|bro|sister|sis|sibling)$/i;
const SIBLING_WORD: Record<string, string> = { brother: 'brother', bro: 'brother', sister: 'sister', sis: 'sister', sibling: 'sibling' };

/**
 * "big brother" / "little sister" -> "brother" / "sister": the ages already say who is older, and a wrong
 * qualifier (a 2-year-old typed as "big brother") was written into the story literally. Anything else is unchanged.
 */
export function normalizeComicRelationship<T extends string | undefined>(relationship: T): T {
  if (!relationship) return relationship;
  const m = SIBLING_RELATIONSHIP.exec(relationship.trim().replace(/\s+/g, ' '));
  return (m ? SIBLING_WORD[m[1].toLowerCase()] : relationship) as T;
}

export const MANGA_PRODUCT_TYPE = 'manga-comic';
export const PICTURE_BOOK_PRODUCT_TYPE = 'picture-book';

export const MANGA_DEFAULTS: MangaSettings = {
  subStyle: 'shonen',
  colorMode: 'full-color',
  readingDirection: 'ltr',
  pageCount: 8,
};

/** Balloon legibility limits enforced by script normalization */
export const MANGA_MAX_WORDS_PER_BALLOON = 12;
export const MANGA_MAX_BALLOONS_PER_PANEL = 3;
export const MANGA_MIN_PANELS_PER_PAGE = 1;
export const MANGA_MAX_PANELS_PER_PAGE = 5;

export const MANGA_SUB_STYLE_INFO: Record<
  MangaSubStyle,
  { name: string; description: string; tradition: ComicTradition }
> = {
  shonen: {
    name: 'Shonen Adventure',
    description: 'Dynamic action lines, bold inking, energetic poses — classic adventure manga.',
    tradition: 'manga',
  },
  shojo: {
    name: 'Shojo Dream',
    description: 'Soft linework, sparkles and flower motifs, expressive large eyes.',
    tradition: 'manga',
  },
  chibi: {
    name: 'Chibi Fun',
    description: 'Super-deformed cute proportions, playful and comedic.',
    tradition: 'manga',
  },
  kodomo: {
    name: 'Kodomo Classic',
    description: 'Simple rounded shapes and clear staging, made for young readers.',
    tradition: 'manga',
  },
  superhero: {
    name: 'Superhero Action',
    description: 'Bold American comic book style — dramatic angles, heroic poses, halftone energy.',
    tradition: 'western',
  },
  cartoon: {
    name: 'Cartoon Strip',
    description: 'Thick friendly outlines and bright flat colors, Saturday-morning cartoon fun.',
    tradition: 'western',
  },
  european: {
    name: 'European Adventure',
    description: 'Clean-line bande dessinée style — detailed backgrounds, warm storybook color.',
    tradition: 'western',
  },
};

/** Tradition for a sub-style; western comics always read left-to-right. */
export function comicTradition(subStyle: MangaSubStyle): ComicTradition {
  return MANGA_SUB_STYLE_INFO[subStyle]?.tradition || 'manga';
}

export function isComicPageCount(n: unknown): n is ComicPageCount {
  return typeof n === 'number' && (COMIC_PAGE_COUNTS as readonly number[]).includes(n);
}

const READER_BANDS: readonly ReaderBand[] = ['T', 'E', 'M', 'O', 'W', 'G'];

const SUB_STYLES = Object.keys(MANGA_SUB_STYLE_INFO) as MangaSubStyle[];

/** A comic sub-style key from a query param (`?style=`), trimmed; exact keys only, anything else is undefined. */
export function mangaSubStyleFromParam(raw: string | null | undefined): MangaSubStyle | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  return SUB_STYLES.includes(s as MangaSubStyle) ? (s as MangaSubStyle) : undefined;
}

/** Coerce untrusted/stored settings into a valid MangaSettings. */
export function resolveMangaSettings(
  partial?: Partial<Record<keyof MangaSettings, unknown>> | null
): MangaSettings {
  const p = partial ?? {};
  const subStyle = SUB_STYLES.includes(p.subStyle as MangaSubStyle)
    ? (p.subStyle as MangaSubStyle)
    : MANGA_DEFAULTS.subStyle;
  const colorMode: MangaColorMode =
    p.colorMode === 'screentone' || p.colorMode === 'full-color' ? p.colorMode : MANGA_DEFAULTS.colorMode;
  let readingDirection: MangaReadingDirection =
    p.readingDirection === 'rtl' || p.readingDirection === 'ltr' ? p.readingDirection : MANGA_DEFAULTS.readingDirection;
  if (comicTradition(subStyle) === 'western') readingDirection = 'ltr';
  const v2Fields: Pick<MangaSettings, 'storyboard' | 'readerBand' | 'pictureBook'> = {
    ...(p.storyboard === 'v2' ? { storyboard: 'v2' as const } : {}),
    ...(READER_BANDS.includes(p.readerBand as ReaderBand) ? { readerBand: p.readerBand as ReaderBand } : {}),
  };
  const pb = p.pictureBook as { style?: unknown; band?: unknown } | null | undefined;
  const pictureBook: PictureBookMode | undefined = pb && typeof pb === 'object' && PICTURE_BOOK_STYLE_IDS.includes(pb.style as PictureBookStyle)
    ? { style: pb.style as PictureBookStyle, ...(READER_BANDS.includes(pb.band as ReaderBand) ? { band: pb.band as ReaderBand } : {}) }
    : undefined;
  if (pictureBook) v2Fields.pictureBook = pictureBook;
  if (pictureBook && p.pageCount === COMIC_SQUARE_PAGE_COUNT) {
    // Picture-book mode: 24 pages are drawn by the picture-book path, never the square-comic shape.
    return { subStyle, colorMode, readingDirection, pageCount: COMIC_SQUARE_PAGE_COUNT, ...v2Fields };
  }
  if (p.pageCount === COMIC_SQUARE_PAGE_COUNT) {
    // Square books are model-lettered: code-lettering geometry has not been checked on square pages.
    return { subStyle, colorMode, readingDirection, pageCount: COMIC_SQUARE_PAGE_COUNT, shape: 'square', lettering: 'model', ...v2Fields };
  }
  const pageCount: ComicPageCount = isComicPageCount(p.pageCount) ? p.pageCount : (MANGA_DEFAULTS.pageCount as ComicPageCount);
  const lettering = p.lettering === 'code' || p.lettering === 'model' ? p.lettering : undefined;
  return { subStyle, colorMode, readingDirection, pageCount, ...(lettering ? { lettering } : {}), ...v2Fields };
}

/** The book's lettering mode: 'model' (today's behaviour) unless the book was stamped 'code'. */
export function letteringMode(settings: Partial<MangaSettings> | undefined): 'code' | 'model' {
  return settings?.lettering === 'code' ? 'code' : 'model';
}

/** Catalog style name for a sub-style. */
export function comicStyleName(subStyle: MangaSubStyle): 'Manga' | 'Graphic Novel' {
  return comicTradition(subStyle) === 'western' ? 'Graphic Novel' : 'Manga';
}
